import React, { Fragment, useEffect, useState } from 'react'
import { BgColorsOutlined, VerticalAlignBottomOutlined } from '@ant-design/icons'
import { Input, Button, Form, notification, Modal } from 'antd'
import { useDispatch } from 'react-redux'
import styles from './Presets.module.scss'
import { useCurrentTheme } from '../../../../../hooks/useCurrentTheme'
import {
	setCardStyle,
	setIndentUsage,
	setNewPreferences,
	setRoundingUsage,
	setShadowUsage,
	setSubstrateUsage,
} from '../../../../../Redux/slices/Preferences/preferences'
import { getAllValueKeys } from './utils'
import ModalButtons from './ModalButtons/ModalButtons'
import { useComponentLocale } from '../../../../../hooks/useComponentLocale'

function ImportColors() {
	const { TextArea } = Input
	const [isShowModal, setIsShowModal] = useState(false)
	const [text, setText] = useState('')
	const [validateStatus, setValidateStatus] = useState('validating')
	const theme = useCurrentTheme()
	const dispatch = useDispatch()

	const getLocale = useComponentLocale()

	const importLocale = getLocale({ component: 'common_buttons', section: 'import' })
	const presetSuccessfullyAppliedLocale = getLocale({ component: 'theme', section: 'preset_successfully_applied' })
	const presetAppliedLocale = getLocale({ component: 'theme', section: 'preset_applied' })
	const useHexFormatLocale = getLocale({ component: 'theme', section: 'use_hex_format' })
	const importYourPresetLocale = getLocale({ component: 'theme', section: 'import_your_preset' })

	const openNotification = () => {
		notification.info({
			message: presetAppliedLocale,
			description: presetSuccessfullyAppliedLocale,
			placement: 'bottomLeft',
			icon: <BgColorsOutlined />,
			duration: 3,
		})
	}

	const handleCancel = () => {
		setIsShowModal(false)
		setText('')
	}

	const handleClearText = () => {
		setText('')
	}

	const handleChangeText = (inputText) => {
		let json
		try {
			json = JSON.parse(inputText)
		} catch (error) {
			setText(inputText)

			return
		}

		setText(JSON.stringify(json, null, '\t'))
	}

	const handlePastePresetData = async () => {
		if (navigator.clipboard.readText) {
			const text = await navigator.clipboard.readText()
			handleChangeText(text)
		}
	}

	const tryPreset = (inputSettings, themeSettings) => {
		const tryColor = (key, type, label = null) => {
			const inputColorsName = Object.keys(inputSettings)
			const value = inputSettings[key]
			const typeOfAction = type === 'cards' ? 'suits' : type

			if (inputColorsName.includes(key)) {
				if (Array.isArray(value) && value.length < 5) {
					for (let index = value.length; index < 5; index++) {
						value.push(theme[type][key][index])
					}
				}

				label !== null
					? dispatch(setNewPreferences(key, value, typeOfAction, label))
					: dispatch(setNewPreferences(key, value, typeOfAction))
			}
		}

		const changeSettings = (currentSettings, type = '') => {
			Object.entries(currentSettings).forEach(([title, value]) => {
				if (title === 'matrix') {
					for (const subtitle in currentSettings[title]) {
						Object.entries(currentSettings[title][subtitle]).forEach(([property]) => {
							tryColor(property, title, subtitle)
						})
					}
				}

				if (title === 'switcher') {
					Object.entries(value).map(([key, property]) => {
						switch (key) {
							case 'rounding':
								dispatch(setRoundingUsage(property))
								break
							case 'indent':
								dispatch(setIndentUsage(property))
								break
							case 'useSubstrate':
								dispatch(setSubstrateUsage(!!property))
								break
							case 'cardLayout':
								dispatch(setCardStyle(property))
								break
							case 'shadow':
								dispatch(setShadowUsage(property))
						}
					})
				}

				if (typeof value === 'string' || Array.isArray(value)) {
					tryColor(title, type)
				}

				if (
					typeof value === 'object' &&
					value !== null &&
					title !== 'hotkeys' &&
					title !== 'matrix' &&
					title !== 'switcher'
				) {
					changeSettings(value, type || title)
				}
			})
		}

		changeSettings(themeSettings)
	}

	const isValidationCorrect = (text) => {
		let preset = null

		try {
			preset = JSON.parse(text)
		} catch (e) {
			return false
		}

		const checkHex = (value) => /^#[0-9A-F]{6}$/i.test(value)

		const validations = Object.entries(preset).map(([key, value]) => {
			if (getAllValueKeys(theme).includes(key)) {
				switch (key) {
					case 'rounding':
					case 'useSubstrate':
						return typeof value === 'boolean'
					case 'indent':
					case 'cardLayout':
					case 'ev':
					case 'equity':
					case 'range':
					case 'strategy':
					case 'cardShirt':
						return typeof value === 'number'
					case 'shadow':
						return typeof value === 'string'
					case 'raise':
						return !value.map((element) => checkHex(element)).includes(false)
					default:
						return checkHex(value)
				}
			} else {
				return false
			}
		})

		return !validations.includes(false)
	}

	const handleTryPreset = () => {
		let preset = null

		if (isValidationCorrect(text)) {
			setValidateStatus('success')
		} else {
			setValidateStatus('error')

			return
		}

		try {
			preset = JSON.parse(text)
		} catch (error) {
			setValidateStatus('error')

			return
		}

		tryPreset(preset, theme)
		setText('')
		setIsShowModal(false)
		openNotification()
	}

	useEffect(() => {
		setValidateStatus('validating')
	}, [text])

	return (
		<>
			<Button className={styles.presetsButton} onClick={() => setIsShowModal(!isShowModal)} size='small'>
				{importLocale}
				{/* <VerticalAlignBottomOutlined /> */}
			</Button>

			<Modal
				title={importYourPresetLocale}
				open={isShowModal}
				onCancel={handleCancel}
				footer={[
					<ModalButtons
						text={text}
						onPastePresetData={handlePastePresetData}
						onClearText={handleClearText}
						onTryPreset={handleTryPreset}
					/>,
				]}
			>
				<Form.Item
					help={validateStatus === 'error' && `${useHexFormatLocale} (#000000)`}
					validateStatus={validateStatus}
					className={styles.inputItem}
				>
					<TextArea
						type='text'
						size='small'
						className={styles.input}
						value={text}
						onChange={(e) => handleChangeText(e.target.value)}
					/>
				</Form.Item>
			</Modal>
		</>
	)
}

export default ImportColors
